import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress
} from '@mui/material';
import { OrderRoom } from '../../types/orderRoom';
import { Order } from '../../types/order';
import { APIHOST } from '../../common';
import { useApp } from '../AppContext';
import {AvailabilityCalendar, HotelCal} from "../../types/availability"



interface AddEditRoomDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  order: Order;
}

export default function AddEditRoomDialog({
  open,
  onClose,
  onSave,
  order
}: AddEditRoomDialogProps) {
  const [dialogRoom, setDialogRoom] = useState<Partial<OrderRoom>>({});
  const { setSnackbarInput } = useApp();
  const [hotelCal, setHotelCal] = useState<HotelCal>({} as HotelCal);
  const [loading, setLoading] = useState<boolean>(true);

  // Initialize dialog room data when opening
  useEffect(() => {
    if (!open) return;
      
    setDialogRoom({
        type: 'student',
        arrivalDate: order?.selectedArrivalDate || '',
        departureDate: order?.selectedDepartureDate || '',
        holdStatus: 'held'
    });
    
    fetchHotelRooms();
  }, [open]);

  const fetchHotelRooms = async () => {
    setLoading(true);
      if (!order.conference_hotel) {
        setHotelCal({} as HotelCal);
        setLoading(false);
        setSnackbarInput({
          message: 'No conference hotel selected for this order',
          severity: 'error'
        });

        return;

      }
      try {
        const authToken = localStorage.getItem('authToken');
        console.log('Fetching conference hotel rooms for order:', order.documentId);
        const response = await fetch(
          `${APIHOST}/api/conferences/${order.conference_group.conference.documentId}/availability`,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.hasOwnProperty(order.conference_hotel.documentId)) {
            setHotelCal(data[order.conference_hotel.documentId]);
            console.debug(data[order.conference_hotel.documentId]);
            setSnackbarInput({
              message: 'Conference hotel availability fetched successfully',
              severity: 'success'
            });

          } else {
            setSnackbarInput({
              message: 'Conference hotel availability not found for selected hotel',
              severity: 'error'
            });
            setHotelCal({} as HotelCal);
          }
            
          
        } else {
          console.error('Error fetching conference hotel rooms:', response.statusText);
          setSnackbarInput({
            message: `Error fetching conference hotel rooms: ${response.statusText}`,
            severity: 'error'
          });
          setHotelCal({} as HotelCal);
         
        }
      } catch (error) {
        console.error('Error fetching conference hotel rooms:', error);
        setHotelCal({} as HotelCal);
      } finally {
        setLoading(false);
      }
    };
    

  const handleDialogRoomChange = (field: keyof OrderRoom, value: any) => {
    // Validate departure date is not before arrival date
    if (field === 'departureDate') {
      if (dialogRoom.arrivalDate && value && new Date(value) <= new Date(dialogRoom.arrivalDate)) {
        return;
      }
    }
    
    // Validate arrival date is not after departure date
    if (field === 'arrivalDate') {
      if (dialogRoom.departureDate && value && new Date(value) >= new Date(dialogRoom.departureDate)) {
        return;
      }
    }

    setDialogRoom(prev => ({ ...prev, [field]: value }));
  };

  const saveRoomFromDialog = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const roomData = {
        ...dialogRoom,
        order: order?.documentId
      };
      
      const { id, order_room_occupants, documentId, createdAt, updatedAt, publishedAt, ...cleanRoomData } = roomData;
      const payload = {
        data: cleanRoomData
      };

      const method = 'POST';
      const url = `${APIHOST}/api/order-rooms`;
      setLoading(true);
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      
      console.debug(`Creating room with data:`, payload); 
      
      if (response.status === 400) {
        const errorData = await response.json();
        console.error('Validation error saving room:', errorData.error.message);
        setSnackbarInput({
          message: errorData.error.message,
          severity: 'error'
        });
        return;
      }
      
      if (response.ok) {
        setSnackbarInput({
          message: 'Room hold added',
          severity: 'success'
        });
        onClose();
        onSave();
      } else {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || 'Failed to save room';
        console.error('Failed to save room:', errorMessage);
        setSnackbarInput({
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving room:', error);
    } finally {

      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {'Add Room'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );

  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {'Add Room'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>Hotel Room</InputLabel>
              <Select
                value={dialogRoom.conference_hotel_room || ''}
                label="Hotel Room"
                onChange={(e) => handleDialogRoomChange('conference_hotel_room', e.target.value)}
                disabled={!order?.conference_hotel || !hotelCal || Object.keys(hotelCal.rooms).length === 0}
              >
                <MenuItem value="">
                  <em>Select a room</em>
                </MenuItem>
                {hotelCal && Object.keys(hotelCal.rooms).map((roomId) => {
                  const hotelRoom = hotelCal.rooms[roomId];
                  return (
                    <MenuItem key={hotelRoom.documentId} value={hotelRoom.documentId}>
                      {hotelRoom.name} - ${hotelRoom.dailyRate}/night
                      {hotelRoom.maxOccupants && ` (Max ${hotelRoom.maxOccupants})`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Room Type</InputLabel>
              <Select
                value={dialogRoom.type || 'student'}
                label="Room Type"
                onChange={(e) => handleDialogRoomChange('type', e.target.value)}
              >
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="adult">Adult</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Arrival Date"
              type="date"
              value={dialogRoom.arrivalDate || ''}
              onChange={(e) => handleDialogRoomChange('arrivalDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: order?.selectedArrivalDate,
                max: order?.selectedDepartureDate
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Departure Date"
              type="date"
              value={dialogRoom.departureDate || ''}
              onChange={(e) => handleDialogRoomChange('departureDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: order?.selectedArrivalDate,
                max: order?.selectedDepartureDate
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={saveRoomFromDialog} 
          variant="contained"
          disabled={!dialogRoom.conference_hotel_room || !dialogRoom.arrivalDate || !dialogRoom.departureDate}
        >
          {'Add Room'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}